import React, { createContext, useContext, useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { Pet } from '../types/pet';
import PetSelectorModal from '../components/PetSelector/PetSelectorModal';
import { createLogger } from '../services/logger';

const logger = createLogger('PetContext');

// Re-export the Pet type for use in other components
export type { Pet };

// Update the context interface
export interface PetContextType {
  pets: Pet[];
  selectedPet: Pet | null;
  setSelectedPet: (petOrId: Pet | string | null) => void;
  loading: boolean;
  error: string | null;
}

const PetContext = createContext<PetContextType>({
  pets: [],
  selectedPet: null,
  setSelectedPet: () => {},
  loading: true,
  error: null
});

export const PetProvider = ({ children }: { children: React.ReactNode }) => {
  const { currentUser } = useAuth();
  const [pets, setPets] = useState<Pet[]>([]);
  const [selectedPet, setSelectedPetState] = useState<Pet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPetId, setSelectedPetId] = useState<string | null>(null);
  const [showPetSelector, setShowPetSelector] = useState(false);

  // Load pets when user changes
  useEffect(() => {
    if (!currentUser) {
      logger.debug('No current user, clearing pets');
      setPets([]);
      setSelectedPetState(null);
      setLoading(false);
      setError(null);
      return;
    }

    logger.debug('Loading pets for user', { userId: currentUser.uid });
    setLoading(true);
    setError(null);
    const q = query(collection(db, 'pets'), where('ownerId', '==', currentUser.uid));
    const unsubscribe = onSnapshot(
      q, 
      (snapshot) => {
        logger.debug('Snapshot received', { 
          size: snapshot.size, 
          empty: snapshot.empty,
          hasPendingWrites: snapshot.metadata.hasPendingWrites 
        });
        
        const petsData = snapshot.docs
          .filter(doc => {
            const data = doc.data();
            // Filter out soft-deleted pets
            if (data.deleted === true) {
              logger.debug('Filtering out soft-deleted pet', { petId: doc.id });
              return false;
            }
            return true;
          })
          .map((doc) => ({ id: doc.id, ...doc.data() } as Pet));
        
        logger.debug('Processed pets', { count: petsData.length, pets: petsData.map(p => ({ id: p.id, name: p.name })) });
        setPets(petsData);
        
        // Check sessionStorage for previously selected pet (within this session)
        const sessionPetId = sessionStorage.getItem('selectedPetId');
        
        if (petsData.length === 1) {
          // Only one pet - auto-select, no modal needed
          logger.debug('Auto-selecting single pet', { petId: petsData[0].id });
          setSelectedPetId(petsData[0].id);
          setShowPetSelector(false);
        } else if (petsData.length > 1) {
          // Multiple pets - check if they selected one this session
          if (sessionPetId && petsData.some(p => p.id === sessionPetId)) {
            // Valid session selection - use it
            logger.debug('Using session-selected pet', { petId: sessionPetId });
            setSelectedPetId(sessionPetId);
            setShowPetSelector(false);
          } else {
            // No valid session selection - show modal
            logger.debug('Multiple pets, showing selector modal');
            setShowPetSelector(true);
          }
        } else {
          logger.debug('No pets found for user');
          setShowPetSelector(false);
        }
        
        setLoading(false);
      },
      (error) => {
        logger.error('Error loading pets', error as Error, { userId: currentUser.uid });
        setError(error.message || 'Failed to load pets');
        setLoading(false);
        setPets([]);
      }
    );
    
    return () => {
      logger.debug('Unsubscribing from pets query');
      unsubscribe();
    };
  }, [currentUser]);

  // Update selected pet when pets change or selectedPetId changes
  useEffect(() => {
    if (selectedPetId && pets.length > 0) {
      const pet = pets.find(p => p.id === selectedPetId);
      if (pet) {
        setSelectedPetState(pet);
      } else if (pets.length > 0) {
        // If selected pet not found, use the first pet
        setSelectedPetId(pets[0].id);
        setSelectedPetState(pets[0]);
      }
    } else if (pets.length > 0 && !selectedPetId) {
      // If no selection but pets exist, select first
      setSelectedPetId(pets[0].id);
      setSelectedPetState(pets[0]);
    } else if (pets.length === 0) {
      // No pets, clear selection
      setSelectedPetState(null);
    }
  }, [pets, selectedPetId]);

  // Function to set selected pet
  const setSelectedPet = (petOrId: Pet | string | null) => {
    if (petOrId === null) {
      setSelectedPetState(null);
      setSelectedPetId(null);
      return;
    }
    
    if (typeof petOrId === 'string') {
      // If string ID was provided, find the pet and set it
      const pet = pets.find(p => p.id === petOrId) || null;
      if (pet) {
        setSelectedPetId(pet.id);
        setSelectedPetState(pet);
      }
    } else {
      // If Pet object was provided, set it directly
      setSelectedPetId(petOrId.id);
      setSelectedPetState(petOrId);
    }
  };

  // Handle pet selection from modal
  const handlePetSelection = (pet: Pet) => {
    setSelectedPet(pet);
    setShowPetSelector(false);
  };

  // Get user's first name for greeting
  const getUserName = () => {
    if (!currentUser) return 'there';
    const displayName = currentUser.displayName || currentUser.email || 'there';
    return displayName.split(' ')[0]; // Get first name
  };

  return (
    <PetContext.Provider value={{ 
      pets, 
      selectedPet, 
      setSelectedPet, 
      loading,
      error
    }}>
      {children}
      
      {/* Pet Selector Modal - shown when user has multiple pets and hasn't selected one this session */}
      {!loading && (
        <PetSelectorModal
          open={showPetSelector}
          pets={pets}
          onSelectPet={handlePetSelection}
          userName={getUserName()}
          showCloseButton={false} // Force selection, don't allow dismissal
        />
      )}
    </PetContext.Provider>
  );
};

export const usePets = () => useContext(PetContext); 